import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  UserPlus, Mail, Lock, User,
  CheckCircle, XCircle, Loader2, AlertCircle,
  Eye, EyeOff, ShieldCheck, LogIn
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { teamService } from '../services/teamService';
import toast from 'react-hot-toast';

const isDev = process.env.NODE_ENV === 'development';
const devLog = (...args) => isDev && console.log(...args);

const AcceptInvitation = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { currentUser: user, loading: authLoading, refreshCustomClaims } = useAuth();

  const [status, setStatus] = useState('loading');
  const [invitationData, setInvitationData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});

  // Cache para evitar re-invocaciones de la callable al re-montar (React Strict Mode, navegación)
  const tokenCacheRef = useRef({ token: null, result: null });

  // Verificar el token al cargar
  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }

    const verifyToken = async () => {
      // Si ya verificamos este token, usar el resultado cacheado
      if (tokenCacheRef.current.token === token && tokenCacheRef.current.result) {
        const cached = tokenCacheRef.current.result;
        devLog('AcceptInvitation: usando resultado cacheado para token');
        setInvitationData(cached);
        setStatus('valid');
        return;
      }

      try {
        const result = await teamService.verifyInvitationToken(token);

        if (!result || !result.valid) {
          setStatus(result?.expired ? 'expired' : 'invalid');
          return;
        }

        const data = {
          token,
          email: result.email,
          restaurantName: result.restaurantName,
          roleName: result.roleName,
          isExistingUser: result.isExistingUser,
        };

        // Guardar en cache para evitar re-invocaciones
        tokenCacheRef.current = { token, result: data };

        setInvitationData(data);
        setStatus('valid');
      } catch (error) {
        devLog('Error verifying token:', error);
        setStatus('error');
      }
    };

    verifyToken();
  }, [token]);

  const validateForm = () => {
    const newErrors = {};

    if (!user && !invitationData?.isExistingUser) {
      if (!formData.name.trim()) {
        newErrors.name = 'El nombre es requerido';
      } else if (formData.name.trim().length < 2) {
        newErrors.name = 'El nombre debe tener al menos 2 caracteres';
      }

      if (!formData.password) {
        newErrors.password = 'La contraseña es requerida';
      } else if (formData.password.length < 6) {
        newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Las contraseñas no coinciden';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAcceptInvitation = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsProcessing(true);

    try {
      const result = await teamService.acceptInvitation(
        token,
        user ? user.displayName || user.email : formData.name,
        user ? null : formData.password
      );

      if (result.success) {
        setStatus('accepted');
        setInvitationData(prev => ({
          ...prev,
          restaurantName: result.data?.restaurantName,
          restaurantId: result.data?.restaurantId
        }));

        toast.success('¡Te has unido al equipo exitosamente!');

        if (user) {
          await refreshCustomClaims();
        }

        setTimeout(() => {
          if (result.data?.restaurantId) {
            navigate(`/${result.data.restaurantId}`);
          } else {
            navigate('/');
          }
        }, 2000);
      }
    } catch (error) {
      devLog('Error accepting invitation:', error);
      
      const errorMessage = error.message || 'Error al aceptar la invitación';
      
      if (errorMessage.includes('expirado') || errorMessage.includes('expired')) {
        setStatus('expired');
      } else if (errorMessage.includes('no encontrada') || errorMessage.includes('not found')) {
        setStatus('invalid');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Ocultar el email parcialmente para privacidad
  const maskEmail = (email) => {
    if (!email) return '';
    const [local, domain] = email.split('@');
    if (local.length <= 3) return `${local[0]}***@${domain}`;
    return `${local.slice(0, 3)}***@${domain}`;
  };

  // --- Estados de pantalla ---

  if (authLoading || status === 'loading') {
    return (
      <div className="min-h-screen bg-surface-app flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={48} className="animate-spin text-kumia-green mx-auto mb-4" />
          <p className="text-content-muted">Verificando invitación...</p>
        </div>
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div className="min-h-screen bg-surface-app flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-surface-1 rounded-2xl p-8 border border-red-500/30 text-center">
          <div className="w-20 h-20 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-6">
            <XCircle size={40} className="text-red-700 dark:text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-content-primary mb-3">Invitación No Válida</h1>
          <p className="text-content-muted mb-6">
            Esta invitación no existe o ya fue utilizada. Por favor, solicita una nueva invitación al administrador.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="btn-kumia-primary w-full"
          >
            Ir al Inicio de Sesión
          </button>
        </div>
      </div>
    );
  }

  if (status === 'expired') {
    return (
      <div className="min-h-screen bg-surface-app flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-surface-1 rounded-2xl p-8 border border-yellow-500/30 text-center">
          <div className="w-20 h-20 rounded-full bg-yellow-50 dark:bg-yellow-900/20 flex items-center justify-center mx-auto mb-6">
            <AlertCircle size={40} className="text-yellow-700 dark:text-yellow-400" />
          </div>
          <h1 className="text-2xl font-bold text-content-primary mb-3">Invitación Expirada</h1>
          <p className="text-content-muted mb-6">
            Esta invitación ha expirado. Por favor, solicita una nueva invitación al administrador del restaurante.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="btn-kumia-primary w-full"
          >
            Ir al Inicio de Sesión
          </button>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-surface-app flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-surface-1 rounded-2xl p-8 border border-red-500/30 text-center">
          <div className="w-20 h-20 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-6">
            <AlertCircle size={40} className="text-red-700 dark:text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-content-primary mb-3">Error de Conexión</h1>
          <p className="text-content-muted mb-6">
            No pudimos verificar la invitación. Revisa tu conexión a internet e intenta de nuevo.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="btn-kumia-primary w-full"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (status === 'accepted') {
    return (
      <div className="min-h-screen bg-surface-app flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-surface-1 rounded-2xl p-8 border border-green-500/30 text-center">
          <div className="w-20 h-20 rounded-full bg-green-50 dark:bg-green-900/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-kumia-green" />
          </div>
          <h1 className="text-2xl font-bold text-content-primary mb-3">¡Bienvenido al Equipo!</h1>
          <p className="text-content-muted mb-6">
            Te has unido exitosamente a <span className="text-kumia-green font-semibold">{invitationData?.restaurantName || 'el restaurante'}</span>. 
            Serás redirigido al dashboard en unos segundos...
          </p>
          <div className="flex items-center justify-center gap-2 text-content-subtle">
            <Loader2 size={16} className="animate-spin" />
            <span>Redirigiendo...</span>
          </div>
        </div>
      </div>
    );
  }

  // --- Vista principal (status === 'valid') ---

  // Caso 1: Usuario YA autenticado → mostrar botón de aceptar
  if (user) {
    return (
      <div className="min-h-screen bg-surface-app flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-kumia-green to-green-600 flex items-center justify-center mx-auto mb-4">
              <UserPlus size={32} className="text-content-primary" />
            </div>
            <h1 className="text-3xl font-bold text-content-primary mb-2">Únete al Equipo</h1>
            {invitationData?.restaurantName && (
              <p className="text-content-muted">
                Has sido invitado a <span className="text-kumia-green font-semibold">{invitationData.restaurantName}</span>
                {invitationData.roleName && <> como <span className="text-purple-700 dark:text-purple-400 font-semibold">{invitationData.roleName}</span></>}
              </p>
            )}
          </div>

          <div className="bg-surface-1 rounded-2xl p-6 border border-line">
            <div className="space-y-6">
              <div className="flex items-center gap-4 p-4 bg-surface-base rounded-xl border border-line">
                <div className="w-12 h-12 rounded-full bg-kumia-green/20 flex items-center justify-center flex-shrink-0">
                  <User size={24} className="text-kumia-green" />
                </div>
                <div className="min-w-0">
                  <p className="text-content-primary font-medium truncate">{user.displayName || 'Usuario'}</p>
                  <p className="text-content-muted text-sm truncate">{user.email}</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-500/30 rounded-xl">
                <ShieldCheck size={20} className="text-blue-700 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <p className="text-blue-700 dark:text-blue-300 text-sm">
                  Tu cuenta existente será vinculada a este restaurante. Mantendrás acceso a todos tus restaurantes actuales.
                </p>
              </div>

              <button
                onClick={handleAcceptInvitation}
                disabled={isProcessing}
                className="btn-kumia-primary w-full flex items-center justify-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <CheckCircle size={18} />
                    Aceptar Invitación
                  </>
                )}
              </button>
            </div>
          </div>

          <p className="text-center text-content-subtle text-sm mt-6">
            © {new Date().getFullYear()} KumIA - Todos los derechos reservados
          </p>
        </div>
      </div>
    );
  }

  // Caso 2: No autenticado + usuario YA EXISTE en el sistema → pedirle que inicie sesión
  if (invitationData?.isExistingUser) {
    return (
      <div className="min-h-screen bg-surface-app flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-kumia-green to-green-600 flex items-center justify-center mx-auto mb-4">
              <UserPlus size={32} className="text-content-primary" />
            </div>
            <h1 className="text-3xl font-bold text-content-primary mb-2">Únete al Equipo</h1>
            {invitationData?.restaurantName && (
              <p className="text-content-muted">
                Has sido invitado a <span className="text-kumia-green font-semibold">{invitationData.restaurantName}</span>
                {invitationData.roleName && <> como <span className="text-purple-700 dark:text-purple-400 font-semibold">{invitationData.roleName}</span></>}
              </p>
            )}
          </div>

          <div className="bg-surface-1 rounded-2xl p-6 border border-line">
            <div className="space-y-6">
              <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-500/30 rounded-xl">
                <Mail size={20} className="text-blue-700 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-blue-700 dark:text-blue-300 text-sm font-medium mb-1">
                    Ya tienes una cuenta en KumIA
                  </p>
                  <p className="text-blue-700 dark:text-blue-300/80 text-sm">
                    Detectamos que el email <span className="text-content-primary font-medium">{maskEmail(invitationData.email)}</span> ya está registrado en nuestro sistema. Inicia sesión con tu cuenta para aceptar la invitación.
                  </p>
                </div>
              </div>

              <button
                onClick={() => navigate('/login', { state: { returnTo: `/accept-invitation/${token}` } })}
                className="btn-kumia-primary w-full flex items-center justify-center gap-2"
              >
                <LogIn size={18} />
                Iniciar Sesión para Aceptar
              </button>
            </div>
          </div>

          <p className="text-center text-content-subtle text-sm mt-6">
            © {new Date().getFullYear()} KumIA - Todos los derechos reservados
          </p>
        </div>
      </div>
    );
  }

  // Caso 3: No autenticado + usuario NUEVO → formulario de crear cuenta
  return (
    <div className="min-h-screen bg-surface-app flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-kumia-green to-green-600 flex items-center justify-center mx-auto mb-4">
            <UserPlus size={32} className="text-content-primary" />
          </div>
          <h1 className="text-3xl font-bold text-content-primary mb-2">Crea tu Cuenta</h1>
          {invitationData?.restaurantName ? (
            <p className="text-content-muted">
              Has sido invitado a unirte a <span className="text-kumia-green font-semibold">{invitationData.restaurantName}</span>
              {invitationData.roleName && <> como <span className="text-purple-700 dark:text-purple-400 font-semibold">{invitationData.roleName}</span></>}
              . Crea tu cuenta para comenzar.
            </p>
          ) : (
            <p className="text-content-muted">
              Has sido invitado a formar parte de un equipo en KumIA. Crea tu cuenta para comenzar.
            </p>
          )}
        </div>

        <div className="bg-surface-1 rounded-2xl p-6 border border-line">
          <form onSubmit={handleAcceptInvitation} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-content-secondary mb-2">
                Tu nombre completo
              </label>
              <div className="relative">
                <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Juan Pérez"
                  className={`w-full bg-surface-base text-content-primary pl-10 pr-4 py-3 rounded-xl border ${
                    errors.name ? 'border-red-500' : 'border-line'
                  } focus:border-kumia-green focus:outline-none focus:ring-1 focus:ring-kumia-green/50`}
                />
              </div>
              {errors.name && (
                <p className="text-red-700 dark:text-red-400 text-xs mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-content-secondary mb-2">
                Contraseña
              </label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Mínimo 6 caracteres"
                  className={`w-full bg-surface-base text-content-primary pl-10 pr-12 py-3 rounded-xl border ${
                    errors.password ? 'border-red-500' : 'border-line'
                  } focus:border-kumia-green focus:outline-none focus:ring-1 focus:ring-kumia-green/50`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-content-muted hover:text-content-primary"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-700 dark:text-red-400 text-xs mt-1">{errors.password}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-content-secondary mb-2">
                Confirmar contraseña
              </label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="Repite tu contraseña"
                  className={`w-full bg-surface-base text-content-primary pl-10 pr-4 py-3 rounded-xl border ${
                    errors.confirmPassword ? 'border-red-500' : 'border-line'
                  } focus:border-kumia-green focus:outline-none focus:ring-1 focus:ring-kumia-green/50`}
                />
              </div>
              {errors.confirmPassword && (
                <p className="text-red-700 dark:text-red-400 text-xs mt-1">{errors.confirmPassword}</p>
              )}
            </div>

            <div className="flex items-start gap-3 p-4 bg-surface-base border border-line rounded-xl">
              <Mail size={18} className="text-content-muted flex-shrink-0 mt-0.5" />
              <p className="text-content-muted text-sm">
                Se creará una cuenta con el email <span className="text-content-primary font-medium">{maskEmail(invitationData?.email)}</span>. Podrás acceder al dashboard inmediatamente.
              </p>
            </div>

            <button
              type="submit"
              disabled={isProcessing}
              className="btn-kumia-primary w-full flex items-center justify-center gap-2"
            >
              {isProcessing ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Creando cuenta...
                </>
              ) : (
                <>
                  <UserPlus size={18} />
                  Crear Cuenta y Unirme
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-content-subtle text-sm mt-6">
          © {new Date().getFullYear()} KumIA - Todos los derechos reservados
        </p>
      </div>
    </div>
  );
};

export default AcceptInvitation;
